import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { useHubEmbedded } from "@/modules/hub-kit/HubKit";
import { ageLabel, formatCount } from "@/modules/hub-kit/format";
import { createCreatorsApi } from "../api";
import { creatorTone, formatLabel } from "../labels";
import { useCreatorQueue } from "../useCreatorQueue";
import type {
  BrandDealDto,
  CampaignDto,
  CreatorApplicationDto,
  CreatorPaymentDto,
  CreatorProfileDto,
  CreatorProgramDto,
  InvitationDto,
  MembershipDto,
  ParticipationDto,
  PerformanceDto,
  TierHistoryDto,
} from "../types";
import {
  CreateApplicationDialog,
  CreateBrandDealDialog,
  CreateCampaignDialog,
  CreateInvitationDialog,
  CreateMembershipDialog,
  CreateParticipationDialog,
  CreatePaymentDialog,
  CreatePerformanceDialog,
  CreateProfileDialog,
  CreateProgramDialog,
  CreateTierDialog,
} from "../components/CreatorDialogs";

function QueueShell({
  title,
  description,
  statuses,
  sortOptions,
  defaultSort,
  columns,
  rows,
  loading,
  error,
  searchDraft,
  setSearchDraft,
  status,
  sort,
  page,
  nextCursor,
  onStatus,
  onSort,
  onSearch,
  onNext,
  onPrevious,
  onRefresh,
  onCreate,
  createLabel,
  extraFilters,
}: {
  title: string;
  description: string;
  statuses: string[];
  sortOptions: { value: string; label: string }[];
  defaultSort: string;
  columns: string[];
  rows: ReactNode;
  loading: boolean;
  error: string | null;
  searchDraft: string;
  setSearchDraft: (value: string) => void;
  status: string;
  sort: string;
  page: number;
  nextCursor: string | null;
  onStatus: (value: string) => void;
  onSort: (value: string) => void;
  onSearch: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
  onCreate: () => void;
  createLabel: string;
  extraFilters?: ReactNode;
}) {
  const embedded = useHubEmbedded();
  usePageBreadcrumbs(
    embedded
      ? embedded.crumbs
      : [
          { label: "HUDDL HQ", to: "/app" },
          { label: "Creator Center", to: "/app/creators" },
          { label: title },
        ],
  );

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={onCreate}>
              {createLabel}
            </Button>
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Search"
            onKeyDown={(event) => {
              if (event.key === "Enter") onSearch();
            }}
          />
        </div>
        <Select
          label="Status"
          value={status}
          onChange={(event) => onStatus(event.target.value)}
          options={[
            { value: "", label: "All statuses" },
            ...statuses.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        {extraFilters}
        <Select
          label="Sort"
          value={sort || defaultSort}
          onChange={(event) => onSort(event.target.value)}
          options={sortOptions}
        />
        <Button variant="primary" onClick={onSearch}>
          Apply search
        </Button>
      </Filters>
      {error ? (
        <Alert tone="danger" title={`Unable to load ${title}`}>
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && !rows ? (
        <EmptyState title={`No ${title} found`} reason="none" />
      ) : null}
      {!loading && rows ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                {columns.map((column) => (
                  <Th key={column}>{column}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>{rows}</Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

function extraQuery(extra: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(extra)) {
    if (value) out[key] = value;
  }
  return out;
}

function makeSimpleQueue<T extends { id: string }>(config: {
  title: string;
  description: string;
  statuses: string[];
  defaultSort: string;
  sortOptions: { value: string; label: string }[];
  columns: string[];
  path: string;
  createLabel: string;
  load: (
    api: ReturnType<typeof createCreatorsApi>,
    params: { cursor?: string; q: string; status: string; sort: string; extra: Record<string, string> },
  ) => Promise<{ items: T[]; pagination: { limit: number; next_cursor: string | null } }>;
  renderRow: (row: T, open: () => void) => ReactNode;
  CreateDialog: typeof CreateProgramDialog;
  create: (
    api: ReturnType<typeof createCreatorsApi>,
    body: Record<string, unknown>,
  ) => Promise<{ id: string }>;
}) {
  return function QueuePage() {
    const client = useApiClient();
    const api = useMemo(() => createCreatorsApi(client), [client]);
    const navigate = useNavigate();
    const toast = useToast();
    const [createOpen, setCreateOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const loadPage = useMemo(
      () =>
        (params: {
          cursor?: string;
          q: string;
          status: string;
          sort: string;
          extra: Record<string, string>;
        }) => config.load(api, params),
      [api],
    );
    const CreateDialog = config.CreateDialog;
    const queue = useCreatorQueue<T>(loadPage);

    return (
      <>
        <QueueShell
          title={config.title}
          description={config.description}
          statuses={config.statuses}
          defaultSort={config.defaultSort}
          sortOptions={config.sortOptions}
          columns={config.columns}
          rows={
            queue.rows.length === 0
              ? null
              : queue.rows.map((row) =>
                  config.renderRow(row, () =>
                    void navigate(`${config.path}/${row.id}`, { state: row }),
                  ),
                )
          }
          loading={queue.loading}
          error={queue.error}
          searchDraft={queue.searchDraft}
          setSearchDraft={queue.setSearchDraft}
          status={queue.status}
          sort={queue.sort}
          page={queue.page}
          nextCursor={queue.nextCursor}
          onStatus={queue.setStatus}
          onSort={queue.setSort}
          onSearch={queue.applySearch}
          onNext={queue.goNext}
          onPrevious={queue.goPrevious}
          onRefresh={() => void queue.load()}
          onCreate={() => setCreateOpen(true)}
          createLabel={config.createLabel}
        />
        <CreateDialog
          open={createOpen}
          busy={busy}
          error={formError}
          onClose={() => {
            setCreateOpen(false);
            setFormError(null);
          }}
          onSubmit={async (body) => {
            setBusy(true);
            setFormError(null);
            try {
              const created = await config.create(api, body);
              toast.push({ title: `${config.title} created`, tone: "success" });
              setCreateOpen(false);
              void navigate(`${config.path}/${created.id}`, { state: created });
            } catch (err) {
              setFormError(apiErrorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        />
      </>
    );
  };
}

export function ProfilesQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        q: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listProfiles({
          limit: 25,
          sort: params.sort || "id",
          ...(params.cursor ? { cursor: params.cursor } : {}),
          ...(params.q ? { q: params.q } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...extraQuery(params.extra),
        });
        return { items: list.creator_profiles, pagination: list.pagination };
      },
    [api],
  );
  const queue = useCreatorQueue<CreatorProfileDto>(loadPage);

  return (
    <>
      <QueueShell
        title="Creator Profiles"
        description="User 1:1 extension. current_tier is summary only."
        statuses={[
          "applicant",
          "active_creator",
          "restricted",
          "suspended",
          "deactivated",
        ]}
        defaultSort="id"
        sortOptions={[
          { value: "status", label: "Status" },
          { value: "id", label: "ID" },
        ]}
        columns={["ID", "User", "Status", "Current tier", "Actions"]}
        rows={
          queue.rows.length === 0
            ? null
            : queue.rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.id}</Td>
                  <Td>{row.user_id}</Td>
                  <Td>
                    <StatusChip tone={creatorTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>{row.current_tier ? formatLabel(row.current_tier) : "—"}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void navigate(`/app/creators/profiles/${row.id}`)}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))
        }
        loading={queue.loading}
        error={queue.error}
        searchDraft={queue.searchDraft}
        setSearchDraft={queue.setSearchDraft}
        status={queue.status}
        sort={queue.sort}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onStatus={queue.setStatus}
        onSort={queue.setSort}
        onSearch={queue.applySearch}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create Profile"
      />
      <CreateProfileDialog
        open={createOpen}
        busy={busy}
        error={formError}
        onClose={() => {
          setCreateOpen(false);
          setFormError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setFormError(null);
          try {
            const result = await api.createProfile(body);
            toast.push({ title: "Creator Profile created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/creators/profiles/${result.creator_profile.id}`);
          } catch (err) {
            setFormError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

/** What Play writes into review_context (lib/creatorApplications.ts). */
function applicantName(row: CreatorApplicationDto): string {
  const name = row.review_context?.legal_name;
  return typeof name === "string" && name.trim() ? name : `Profile ${row.creator_profile_id.slice(0, 8)}`;
}

function audienceLabel(row: CreatorApplicationDto): string {
  // Since Play Polishing 18.3 an application carries links, not follower counts.
  const links = row.review_context?.links;
  if (Array.isArray(links) && links.length > 0) return `${links.length} link${links.length === 1 ? "" : "s"}`;
  const platforms = row.review_context?.platforms;
  if (!Array.isArray(platforms) || platforms.length === 0) return "—";
  let followers = 0;
  for (const p of platforms) {
    const n = (p as { followers?: unknown }).followers;
    if (typeof n === "number" && Number.isFinite(n)) followers += n;
  }
  return `${formatCount(followers)} followers · ${platforms.length} platform${platforms.length === 1 ? "" : "s"}`;
}

export function ApplicationsQueuePage() {
  const client = useApiClient();
  const api = useMemo(() => createCreatorsApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        q: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listApplications({
          limit: 25,
          sort: params.sort || "-submitted_at",
          ...(params.cursor ? { cursor: params.cursor } : {}),
          ...(params.q ? { q: params.q } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...extraQuery(params.extra),
        });
        return { items: list.creator_applications, pagination: list.pagination };
      },
    [api],
  );
  const queue = useCreatorQueue<CreatorApplicationDto>(loadPage);

  return (
    <>
      <QueueShell
        title="Applications"
        description="Completeness, assignment, evidence, then decide. Verified is delegated."
        statuses={[
          "draft",
          "submitted",
          "under_review",
          "more_information_required",
          "on_hold",
          "approved",
          "conditionally_approved",
          "rejected",
          "withdrawn",
          "closed",
        ]}
        defaultSort="-submitted_at"
        sortOptions={[
          { value: "-submitted_at", label: "Submitted (newest)" },
          { value: "status", label: "Status" },
          { value: "id", label: "ID" },
        ]}
        columns={["Applicant", "Tier", "Status", "Audience", "Submitted", "Reviewer", "Actions"]}
        rows={
          queue.rows.length === 0
            ? null
            : queue.rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{applicantName(row)}</Td>
                  <Td>{formatLabel(row.application_type)}</Td>
                  <Td>
                    <StatusChip tone={creatorTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>{audienceLabel(row)}</Td>
                  <Td>
                    {row.submitted_at
                      ? `${new Date(row.submitted_at).toLocaleDateString("en-PH")} · ${ageLabel(row.submitted_at) ?? ""} ago`
                      : "Not submitted"}
                  </Td>
                  <Td>{row.assigned_reviewer_user_id ? "Assigned" : "Unassigned"}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void navigate(`/app/creators/applications/${row.id}`)}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
              ))
        }
        loading={queue.loading}
        error={queue.error}
        searchDraft={queue.searchDraft}
        setSearchDraft={queue.setSearchDraft}
        status={queue.status}
        sort={queue.sort}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onStatus={queue.setStatus}
        onSort={queue.setSort}
        onSearch={queue.applySearch}
        onNext={queue.goNext}
        onPrevious={queue.goPrevious}
        onRefresh={() => void queue.load()}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create Application"
      />
      <CreateApplicationDialog
        open={createOpen}
        busy={busy}
        error={formError}
        onClose={() => {
          setCreateOpen(false);
          setFormError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setFormError(null);
          try {
            const result = await api.createApplication(body);
            toast.push({ title: "Application created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/creators/applications/${result.creator_application.id}`);
          } catch (err) {
            setFormError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export const TierHistoryQueuePage = makeSimpleQueue<TierHistoryDto>({
  title: "Tier History",
  description: "Authoritative tier records. Activating a row closes the prior active row.",
  statuses: [
    "not_assigned",
    "pending",
    "active",
    "conditionally_active",
    "suspended",
    "revoked",
    "expired_or_due_for_review",
  ],
  defaultSort: "-effective_from",
  sortOptions: [
    { value: "-effective_from", label: "Effective (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Tier", "Status", "Profile", "Effective from", "Actions"],
  path: "/app/creators/tier-history",
  createLabel: "Create tier row",
  load: async (api, params) => {
    const list = await api.listTierHistory({
      limit: 25,
      sort: params.sort || "-effective_from",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.tier_history, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{formatLabel(row.tier)}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>{row.effective_from}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateTierDialog,
  create: async (api, body) => {
    const result = await api.createTierHistory(body);
    return result.tier_history;
  },
});

export const ProgramsQueuePage = makeSimpleQueue<CreatorProgramDto>({
  title: "Programs",
  description: "Owner, eligibility, review cadence, memberships, and campaigns.",
  statuses: ["proposed", "approved", "active", "paused", "retired"],
  defaultSort: "name",
  sortOptions: [
    { value: "name", label: "Name" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["Name", "Status", "Owner", "Market", "Actions"],
  path: "/app/creators/programs",
  createLabel: "Create Program",
  load: async (api, params) => {
    const list = await api.listPrograms({
      limit: 25,
      sort: params.sort || "name",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.creator_programs, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.name}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.owner_user_id ?? "—"}</Td>
      <Td>{row.market_id ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateProgramDialog,
  create: async (api, body) => {
    const result = await api.createProgram(body);
    return result.creator_program;
  },
});

export const MembershipsQueuePage = makeSimpleQueue<MembershipDto>({
  title: "Memberships",
  description: "One active membership per program and profile.",
  statuses: [
    "invited",
    "nominated",
    "pending",
    "active",
    "on_hold",
    "completed",
    "removed",
    "expired",
  ],
  defaultSort: "-effective_from",
  sortOptions: [
    { value: "-effective_from", label: "Effective (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Program", "Profile", "Status", "Actions"],
  path: "/app/creators/memberships",
  createLabel: "Create membership",
  load: async (api, params) => {
    const list = await api.listMemberships({
      limit: 25,
      sort: params.sort || "-effective_from",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.memberships, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_program_id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateMembershipDialog,
  create: async (api, body) => {
    const result = await api.createMembership(body);
    return result.membership;
  },
});

export const CampaignsQueuePage = makeSimpleQueue<CampaignDto>({
  title: "Campaigns",
  description: "Invitation response and campaign participation stay separate.",
  statuses: ["draft", "pending_approval", "scheduled", "active", "completed", "cancelled"],
  defaultSort: "name",
  sortOptions: [
    { value: "name", label: "Name" },
    { value: "-schedule_start_at", label: "Start (newest)" },
    { value: "status", label: "Status" },
  ],
  columns: ["Name", "Status", "Program", "Owner", "Actions"],
  path: "/app/creators/campaigns",
  createLabel: "Create Campaign",
  load: async (api, params) => {
    const list = await api.listCampaigns({
      limit: 25,
      sort: params.sort || "name",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.campaigns, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.name}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.creator_program_id ?? "—"}</Td>
      <Td>{row.owner_user_id ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateCampaignDialog,
  create: async (api, body) => {
    const result = await api.createCampaign(body);
    return result.campaign;
  },
});

export const InvitationsQueuePage = makeSimpleQueue<InvitationDto>({
  title: "Invitations",
  description: "Invitation status is not participation. Accept may create a pending participation.",
  statuses: [
    "draft",
    "sent",
    "viewed",
    "accepted",
    "declined",
    "expired",
    "withdrawn",
    "completed",
  ],
  defaultSort: "-sent_at",
  sortOptions: [
    { value: "-sent_at", label: "Sent (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Campaign", "Profile", "Status", "Actions"],
  path: "/app/creators/invitations",
  createLabel: "Create Invitation",
  load: async (api, params) => {
    const list = await api.listInvitations({
      limit: 25,
      sort: params.sort || "-sent_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.invitations, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_campaign_id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateInvitationDialog,
  create: async (api, body) => {
    const result = await api.createInvitation(body);
    return result.invitation;
  },
});

export const ParticipationsQueuePage = makeSimpleQueue<ParticipationDto>({
  title: "Participations",
  description: "Campaign participation stays independent of invitation response.",
  statuses: ["pending", "active", "completed", "cancelled", "removed"],
  defaultSort: "-started_at",
  sortOptions: [
    { value: "-started_at", label: "Started (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Campaign", "Profile", "Invitation", "Status", "Actions"],
  path: "/app/creators/participations",
  createLabel: "Create Participation",
  load: async (api, params) => {
    const list = await api.listParticipations({
      limit: 25,
      sort: params.sort || "-started_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.participations, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_campaign_id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>{row.creator_invitation_id ?? "—"}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateParticipationDialog,
  create: async (api, body) => {
    const result = await api.createParticipation(body);
    return result.participation;
  },
});

export const BrandDealsQueuePage = makeSimpleQueue<BrandDealDto>({
  title: "Brand Deals",
  description: "Lean future-capability status. Not a commercial payment ledger.",
  statuses: [
    "proposed",
    "under_review",
    "approved",
    "active",
    "completed",
    "cancelled",
    "disputed",
    "closed",
  ],
  defaultSort: "id",
  sortOptions: [
    { value: "-starts_at", label: "Start (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Profile", "Status", "Owner", "Actions"],
  path: "/app/creators/brand-deals",
  createLabel: "Record Brand Deal",
  load: async (api, params) => {
    const list = await api.listBrandDeals({
      limit: 25,
      sort: params.sort || "id",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.brand_deals, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.commercial_owner_user_id ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreateBrandDealDialog,
  create: async (api, body) => {
    const result = await api.createBrandDeal(body);
    return result.brand_deal;
  },
});

export const PerformanceQueuePage = makeSimpleQueue<PerformanceDto>({
  title: "Performance",
  description: "Evidence for review. Not an independent tier.",
  statuses: ["measured", "reviewed", "actioned", "superseded"],
  defaultSort: "-period_start_at",
  sortOptions: [
    { value: "-period_start_at", label: "Period (newest)" },
    { value: "status", label: "Status" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Profile", "Status", "Period start", "Actions"],
  path: "/app/creators/performance",
  createLabel: "Create record",
  load: async (api, params) => {
    const list = await api.listPerformance({
      limit: 25,
      sort: params.sort || "-period_start_at",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.performance_records, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>{row.period_start_at}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreatePerformanceDialog,
  create: async (api, body) => {
    const result = await api.createPerformance(body);
    return result.performance_record;
  },
});

export const PaymentsQueuePage = makeSimpleQueue<CreatorPaymentDto>({
  title: "Creator Payments",
  description: "Eligibility and history only. Finance owns execution. Optional payout link does not change payout status.",
  statuses: [
    "eligible",
    "pending_review",
    "approved",
    "processing",
    "paid",
    "settled",
    "held",
    "failed",
    "disputed",
    "cancelled",
  ],
  defaultSort: "id",
  sortOptions: [
    { value: "status", label: "Status" },
    { value: "amount", label: "Amount" },
    { value: "id", label: "ID" },
  ],
  columns: ["ID", "Profile", "Status", "Amount", "Payout", "Actions"],
  path: "/app/creators/payments",
  createLabel: "Record eligibility",
  load: async (api, params) => {
    const list = await api.listPayments({
      limit: 25,
      sort: params.sort || "id",
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...extraQuery(params.extra),
    });
    return { items: list.payments, pagination: list.pagination };
  },
  renderRow: (row, open) => (
    <Tr key={row.id}>
      <Td>{row.id}</Td>
      <Td>{row.creator_profile_id}</Td>
      <Td>
        <StatusChip tone={creatorTone(row.status)}>{formatLabel(row.status)}</StatusChip>
      </Td>
      <Td>
        {row.amount} {row.currency}
      </Td>
      <Td>{row.payout_id ?? "—"}</Td>
      <Td>
        <Button size="sm" variant="primary" onClick={open}>
          Open
        </Button>
      </Td>
    </Tr>
  ),
  CreateDialog: CreatePaymentDialog,
  create: async (api, body) => {
    const result = await api.createPayment(body);
    return result.payment;
  },
});
